import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { AbstractControl, FormGroup, Validators } from '@angular/forms';
import { MatStepper } from '@angular/material/stepper';
import { isEqual } from 'lodash-es';

import { TranslateService } from '@ngx-translate/core';
import { lastValueFrom, ReplaySubject } from 'rxjs';
import { debounceTime, delay, skip, take, takeUntil } from 'rxjs/operators';

import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { MatButton } from '@angular/material/button';
import { AvailableLanguages, patterns } from '@constants';
import { DeviceInfoService } from '@core/services/device-info.service';
import { LocalStorageService } from '@core/services/local-storage.service';
import {
  AllDeviceInfo,
  GUIDE_NAME,
  GuideFinishedOpts,
  PersonalDetails,
  PROCESS_CATEGORY,
  UserProcess,
} from '@interfaces';
import { LayoutService } from '@layout/layout.service';
import {
  customDateValidator,
  forbiddenDateInPast,
  forbiddenFutureDate,
  isBooleanValidator,
  isNumericValidator,
} from '@shared/custom-validators';
import { IntroModalService, MODAL_MESSAGE } from '@shared/intro-modal/intro-modal.service';
import { ModeChangedModalService } from '@shared/mode-changed-modal/mode-changed-modal.service';

type DateFormats = {
  [key: string]: string;
};
type FormStep = 'step1' | 'step2' | 'step3' | 'step4' | 'step5' | 'step6';

@Component({
  selector: 'app-long-resi-form',
  templateUrl: './long-resi-form.component.html',
  styleUrls: ['./long-resi-form.component.scss'],
  providers: [
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { displayDefaultIndicatorType: false },
    },
  ],
})
export class LongResiFormComponent implements OnInit, AfterViewInit, OnDestroy {
  private destroy$: ReplaySubject<boolean> = new ReplaySubject(1);

  @ViewChild('mainStepper') mainStepper: MatStepper;
  @ViewChild('stepperContainer') stepperContainer: ElementRef;
  @ViewChild('saveAndProceedButton') saveAndProceedButton: MatButton;

  @Input() userProcess: UserProcess;
  @Input() currentLang: AvailableLanguages;
  @Input() loadingUserProcess: boolean;
  @Input() errorMessage: string;
  @Input() mode: 'foreigner' | 'employee';

  @Input() correctlyFilledSteps: number;
  @Input() personalDataForm: FormGroup<{ [key in FormStep]: FormGroup }>;
  @Input() percentageOfCompletness: number;
  @Input() totalStepsInForm: number;
  @Input() dateFormatsForFields: DateFormats;

  @Output() updateDetails = new EventEmitter<PersonalDetails>();
  @Output() confirmDetails = new EventEmitter<void>();
  @Output() confirmVoivoChange = new EventEmitter<void>();
  @Output() toggleVerificationOfPersonalDetails = new EventEmitter();

  public isSmallView: boolean = false;

  public stepper: MatStepper;
  public GUIDE_NAME = GUIDE_NAME;
  public deviceInfo: AllDeviceInfo;

  constructor(
    private readonly translateService: TranslateService,
    private readonly layoutService: LayoutService,
    private readonly introModal: IntroModalService,
    private readonly localStorage: LocalStorageService,
    private readonly modeChangedModal: ModeChangedModalService,
    private readonly deviceInfoService: DeviceInfoService
  ) {}

  ngOnInit(): void {
    this.deviceInfo = this.deviceInfoService.getInfo();

    this.deviceInfoService.infoEmitter.pipe(takeUntil(this.destroy$)).subscribe(info => {
      this.deviceInfo = info;
    });

    this.checkViewport();

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.checkViewport.bind(this));
    }
  }

  public async ngAfterViewInit(): Promise<void> {
    this.mainStepper?.selectionChange.pipe(takeUntil(this.destroy$)).subscribe(({ previouslySelectedIndex }) => {
      const subFormKey = `step${previouslySelectedIndex + 1}` as FormStep;
      this.personalDataForm.get(subFormKey).markAllAsTouched();
    });

    if (this.personalDataForm) {
      setTimeout(() => {
        this.registerValueChangesHandler();
        this.addStep3ValidationChanges(this.personalDataForm.controls['step3'].controls);
        this.addStep4ValidationChanges(this.personalDataForm.controls['step4'].controls);
      });
    }

    if (this.mode !== 'foreigner') {
      return;
    }
    const wasLoggedInBefore = this.localStorage.getFirstLogin();
    const hasSeenPersonalDataForm = this.localStorage.getSeenPersonalDataForm(this.userProcess.id);
    const hasSeenPersonalDataFormWithoutProcess = this.localStorage.getSeenPersonalDataForm();

    if (!wasLoggedInBefore) {
      await lastValueFrom(
        this.introModal
          .open({ messageKey: MODAL_MESSAGE.USER_WELCOME, showActionButtons: false })
          .afterClosed()
          .pipe(take(1))
      );
    }

    if (!wasLoggedInBefore && this.deviceInfo.deviceTypeDetected === 'DESKTOP') {
      await lastValueFrom(this.layoutService.startGuide(GUIDE_NAME.FIRST_TIME, this.translateService).pipe(take(1)));
    }

    if (!hasSeenPersonalDataForm && !hasSeenPersonalDataFormWithoutProcess) {
      await lastValueFrom(
        this.introModal
          .open({ messageKey: MODAL_MESSAGE.PERSONAL_DETAILS_INSTRUCTIONS, showActionButtons: false })
          .afterClosed()
          .pipe(take(1))
      );
    }

    if (
      !hasSeenPersonalDataForm &&
      !hasSeenPersonalDataFormWithoutProcess &&
      this.deviceInfo.deviceTypeDetected === 'DESKTOP'
    ) {
      await this.startPersonalDataFormGuide(GUIDE_NAME.PERSONAL_DATA_FORM, 1);
    }

    this.localStorage.setFirstLogin();
    this.localStorage.setSeenPersonalDataForm(this.userProcess.id, true);

    this.modeChangedModal.openIfPostModeAndNotSeen({ userProcess: this.userProcess });
  }

  ngOnDestroy(): void {
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.checkViewport.bind(this));
    }

    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public async initGuide(name: GUIDE_NAME, guideOfStep: number): Promise<void> {
    if (this.deviceInfo.deviceTypeDetected === 'DESKTOP') {
      await lastValueFrom(
        this.introModal
          .open({ messageKey: MODAL_MESSAGE.PERSONAL_DETAILS_INSTRUCTIONS, showActionButtons: false })
          .afterClosed()
          .pipe(take(1), takeUntil(this.destroy$))
      );
      this.startPersonalDataFormGuide(name, guideOfStep, PROCESS_CATEGORY.LONG_RESI);
    }
  }

  public async startPersonalDataFormGuide(
    name: GUIDE_NAME,
    guideOfStep: number,
    processCategory?: PROCESS_CATEGORY
  ): Promise<GuideFinishedOpts> {
    if (this.mainStepper.selectedIndex + 1 !== guideOfStep) {
      await lastValueFrom(this.mainStepper.animationDone.pipe(take(1), takeUntil(this.destroy$)));
    }

    const guideFinishedOpts = await lastValueFrom(
      this.layoutService
        .startGuide(name, this.translateService, processCategory)
        .pipe(take(1), takeUntil(this.destroy$))
    );

    this.localStorage.setPersonalDetailsFormStepSeen(guideOfStep, true);
    return guideFinishedOpts;
  }

  public stepAnimationDone(): void {
    const { selectedIndex } = this.mainStepper;
    const indexToId = [
      'applicantName',
      'telephoneNumberInput',
      'meansOfSubsistence-0-input',
      'inPolandAtApplicationSubmition-0-input',
      'applicantNoFamilyMembersInPoland-0-input',
      'rationaleOfTheApplicationInput',
    ];

    const firstElemId = indexToId[selectedIndex];
    const firstElem = document.getElementById(firstElemId);

    if (!firstElem) {
      return;
    }

    setTimeout(() => firstElem.scrollIntoView({ block: 'center', behavior: 'smooth' }), 50);

    // Do not focus any INPUT if this is EMPLOYEE
    if (this.mode !== 'foreigner') {
      return;
    }

    setTimeout(() => {
      firstElem.focus({ preventScroll: false });

      // Do we really need this?
      //  -- it stops scrolling if there was a lot of scroll to be done (because we're not adding 'preventScroll: true')
      // firstElem.dispatchEvent(new Event('focus'));

      // if element ID contains '-' then it means that it is a radio button
      // if it is a radio button then we want to add 'cdk-keyboard-focused'
      // to enforce having different appearance
      if (firstElemId.includes('-')) {
        firstElem.classList.add('cdk-keyboard-focused');
      }
    }, 150);
  }

  // Validating all of user inputs and setting stepState of all steps
  public saveAndProceed(): void {
    this.personalDataForm.markAllAsTouched();
    this.personalDataForm.markAsDirty();
    this.confirmDetails.emit();
  }

  private registerValueChangesHandler(): void {
    this.personalDataForm.valueChanges.pipe(skip(1), debounceTime(500), takeUntil(this.destroy$)).subscribe(changes => {
      const dataToSend = {} as any;
      const stepsGroups = { ...changes };
      Object.values(stepsGroups).forEach(group => {
        Object.entries(group).forEach(([fieldName, fieldValue]: [string, any]) => {
          const dateFormat: string | undefined = this.dateFormatsForFields[fieldName];

          if (fieldValue?._isAMomentObject && dateFormat) {
            dataToSend[fieldName] = fieldValue.format(dateFormat);
          } else {
            dataToSend[fieldName] = fieldValue;
          }

          if (fieldName === 'descriptionHeight' && fieldValue) {
            dataToSend['descriptionHeight'] = Math.floor(fieldValue);
          }
        });
      });

      this.correctlyFilledSteps = 0;
      Object.values(this.personalDataForm.controls).forEach(formGroup => {
        if (formGroup.valid) {
          this.correctlyFilledSteps += 1;
        }
      });
      this.percentageOfCompletness = Math.ceil((this.correctlyFilledSteps / this.totalStepsInForm) * 100);
      setTimeout(() => this.layoutService.progressPercentage$.next(this.percentageOfCompletness));

      const personalDetails = this.userProcess.personalDetails;
      const differences = [] as any;
      Object.keys(this.userProcess.personalDetails).forEach((udk: any) => {
        const original = personalDetails[udk as keyof PersonalDetails];
        const changed = dataToSend[udk];
        if (dataToSend[udk] !== undefined && !isEqual(original, changed)) {
          differences.push({
            key: udk,
            original,
            changed,
          });
        }
      });

      if (!differences.length) {
        return;
      }

      this.updateDetails.emit(dataToSend);
    });
  }

  private addStep3ValidationChanges(controls: { [key: string]: AbstractControl }): void {
    const meansOfSubsistenceValue = controls.meansOfSubsistence.value;
    this.toggleMeansOfSubsistence(meansOfSubsistenceValue);

    controls.meansOfSubsistence.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(meansOfSubsistenceValue => {
      this.toggleMeansOfSubsistence(meansOfSubsistenceValue);
    });
  }

  private addStep4ValidationChanges(controls: { [key: string]: AbstractControl }): void {
    const inPolandAtApplicationSubmition = controls.inPolandAtApplicationSubmition.value;
    this.toggleRequiredOfCurrentlyLivingInPolandAndBasisOfStay(inPolandAtApplicationSubmition);

    const hasBlueCardStaysInEU = controls.hasBlueCardStaysInEU.value;
    this.toggleApplicantHaveBlueCardStaysInEU(hasBlueCardStaysInEU);

    const hasScientistStaysInEU = controls.hasScientistStaysInEU.value;
    this.toggleApplicantHaveScientistStaysInEU(hasScientistStaysInEU);

    const hasInternationalProtectionStaysInEU = controls.hasInternationalProtectionStaysInEU.value;
    this.toggleApplicantHaveInternationalProtectionStaysInEU(hasInternationalProtectionStaysInEU);

    const hasHighQualificationsStaysInEU = controls.hasHighQualificationsStaysInEU.value;
    this.toggleApplicantHaveHighQualificationsStaysInEU(hasHighQualificationsStaysInEU);

    const hasStudentStaysInEU = controls.hasStudentStaysInEU.value;
    this.toggleApplicantHaveStudentStaysInEU(hasStudentStaysInEU);

    const hasTravelsWithBlueCardStaysInEU = controls.hasTravelsWithBlueCardStaysInEU.value;
    this.toggleApplicantHaveTravelsWithBlueCardStaysInEU(hasTravelsWithBlueCardStaysInEU);

    const hasOtherLongTermPermits = controls.hasOtherLongTermPermits.value;
    this.toggleApplicantOtherLongTermPermits(hasOtherLongTermPermits);

    const currentlyLivingInPolandValue = controls.currentlyLivingInPoland.value;
    this.toggleCurrentlyLivingInPoland(currentlyLivingInPolandValue);

    const shouldPreviousVisitsBeRequired = controls.noPreviousVisits.value === false;
    this.toggleRequiredOfPreviousVisits(shouldPreviousVisitsBeRequired);

    const legalBasisOfStay = controls.legalBasisOfStay.value;
    this.toggleRequiredOfLegalBasisOfStay(legalBasisOfStay);
    // if (legalBasisOfStay === 'VISA') {
    //   this.toggleRequiredOfLegalBasisOfStay('VISA');
    // }
    // if (legalBasisOfStay === 'SCHENGEN_COUNTRY_ISSUED_DOC') {
    //   this.toggleRequiredOfLegalBasisOfStay('SCHENGEN_COUNTRY_ISSUED_DOC');
    // }

    controls.inPolandAtApplicationSubmition.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(inPLAtSubmition => {
      this.toggleRequiredOfCurrentlyLivingInPolandAndBasisOfStay(inPLAtSubmition);
    });

    controls.currentlyLivingInPoland.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.clearBasisOfStay();
    });

    controls.legalBasisOfStay.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(legalBasisOfStay => {
      this.toggleRequiredOfLegalBasisOfStay(legalBasisOfStay);
    });

    controls.noPreviousVisits.valueChanges.pipe(delay(50), takeUntil(this.destroy$)).subscribe(noPreviousVisits => {
      const previousVisitsRequired = noPreviousVisits === false;
      this.toggleRequiredOfPreviousVisits(previousVisitsRequired);
    });

    controls.hasBlueCardStaysInEU.valueChanges
      .pipe(delay(50), takeUntil(this.destroy$))
      .subscribe(hasBlueCardStaysInEU => {
        this.toggleApplicantHaveBlueCardStaysInEU(hasBlueCardStaysInEU);
      });

    controls.hasScientistStaysInEU.valueChanges
      .pipe(delay(50), takeUntil(this.destroy$))
      .subscribe(hasScientistStaysInEU => {
        this.toggleApplicantHaveScientistStaysInEU(hasScientistStaysInEU);
      });

    controls.hasInternationalProtectionStaysInEU.valueChanges
      .pipe(delay(50), takeUntil(this.destroy$))
      .subscribe(hasInternationalProtectionStaysInEU => {
        this.toggleApplicantHaveInternationalProtectionStaysInEU(hasInternationalProtectionStaysInEU);
      });

    controls.hasHighQualificationsStaysInEU.valueChanges
      .pipe(delay(50), takeUntil(this.destroy$))
      .subscribe(hasHighQualificationsStaysInEU => {
        this.toggleApplicantHaveHighQualificationsStaysInEU(hasHighQualificationsStaysInEU);
      });

    controls.hasStudentStaysInEU.valueChanges
      .pipe(delay(50), takeUntil(this.destroy$))
      .subscribe(hasStudentStaysInEU => {
        this.toggleApplicantHaveStudentStaysInEU(hasStudentStaysInEU);
      });

    controls.hasTravelsWithBlueCardStaysInEU.valueChanges
      .pipe(delay(50), takeUntil(this.destroy$))
      .subscribe(hasTravelsWithBlueCardStaysInEU => {
        this.toggleApplicantHaveTravelsWithBlueCardStaysInEU(hasTravelsWithBlueCardStaysInEU);
      });

    controls.hasOtherLongTermPermits.valueChanges
      .pipe(delay(50), takeUntil(this.destroy$))
      .subscribe(hasOtherLongTermPermits => {
        this.toggleApplicantOtherLongTermPermits(hasOtherLongTermPermits);
      });

    controls.currentlyLivingInPoland.valueChanges
      .pipe(delay(50), takeUntil(this.destroy$))
      .subscribe(currentlyLivingInPolandValue => {
        this.toggleCurrentlyLivingInPoland(currentlyLivingInPolandValue);
      });
  }

  private toggleMeansOfSubsistence(meansOfSubsistence: string): void {
    const step3Controls = this.personalDataForm.controls['step3'].controls;
    const meansOfSubsistenceLc = meansOfSubsistence?.toLowerCase();

    if (meansOfSubsistenceLc === 'inne') {
      step3Controls.meansOfSubsistenceOthers.setValidators([Validators.required, Validators.maxLength(200)]);
    }

    if (meansOfSubsistenceLc !== 'inne') {
      step3Controls.meansOfSubsistenceOthers.setValidators([Validators.maxLength(200)]);
      step3Controls.meansOfSubsistenceOthers.reset(null);
    }

    step3Controls.meansOfSubsistenceOthers.updateValueAndValidity();
  }

  private toggleRequiredOfCurrentlyLivingInPolandAndBasisOfStay(inPolandAtApplicationSubmition: boolean): void {
    const step4Controls = this.personalDataForm.controls['step4'].controls;

    if (!inPolandAtApplicationSubmition) {
      step4Controls.currentlyLivingInPoland.setValidators([isBooleanValidator]);
      step4Controls.currentlyLivingInPoland.reset(null);

      step4Controls.legalBasisOfStay.setValidators([Validators.maxLength(1000)]);
      step4Controls.legalBasisOfStay.reset(null);

      step4Controls.residenceVisaExpiryDate.setValidators([customDateValidator]);
      step4Controls.residenceVisaExpiryDate.reset(null);

      step4Controls.lastEntryDate.setValidators([customDateValidator]);
      step4Controls.lastEntryDate.reset(null);

      step4Controls.purposeOfStay.setValidators([Validators.maxLength(100)]);
      step4Controls.purposeOfStay.reset(null);
    }

    if (inPolandAtApplicationSubmition) {
      step4Controls.currentlyLivingInPoland.setValidators([Validators.required, isBooleanValidator]);

      step4Controls.legalBasisOfStay.setValidators([Validators.required, Validators.maxLength(1000)]);
      step4Controls.residenceVisaExpiryDate.setValidators([Validators.required, customDateValidator]);
      step4Controls.lastEntryDate.setValidators([Validators.required, customDateValidator]);
      step4Controls.purposeOfStay.setValidators([Validators.required, Validators.maxLength(100)]);
    }

    step4Controls.currentlyLivingInPoland.updateValueAndValidity();

    step4Controls.legalBasisOfStay.updateValueAndValidity();
    step4Controls.residenceVisaExpiryDate.updateValueAndValidity();
    step4Controls.lastEntryDate.updateValueAndValidity();
    step4Controls.purposeOfStay.updateValueAndValidity();
  }

  private clearBasisOfStay(): void {
    const step4Controls = this.personalDataForm.controls['step4'].controls;

    step4Controls.legalBasisOfStay.reset(null);
    step4Controls.residenceVisaExpiryDate.reset(null);
    step4Controls.lastEntryDate.reset(null);
    step4Controls.legalBasisOfStayComment.reset(null);
    step4Controls.purposeOfStay.reset(null);
  }

  private toggleRequiredOfPreviousVisits(shouldBeRequired: boolean): void {
    const step4Controls = this.personalDataForm.controls['step4'].controls;
    if (shouldBeRequired) {
      step4Controls.previousVisitsArr.setValidators([
        Validators.required,
        // Validators.maxLength(1000),
        // Validators.pattern(patterns.PLTextValidator),
      ]);
    }

    if (!shouldBeRequired) {
      step4Controls.previousVisitsArr.setValidators([
        // Validators.maxLength(1000),
        // Validators.pattern(patterns.PLTextValidator),
      ]);
    }

    step4Controls.previousVisitsArr.updateValueAndValidity();
  }

  private toggleRequiredOfLegalBasisOfStay(legalBasisOfStay: string): void {
    const step4Controls = this.personalDataForm.controls['step4'].controls;

    if (legalBasisOfStay === 'VISA') {
      step4Controls.stayVisaType.setValidators([Validators.required, Validators.maxLength(100)]);
      step4Controls.stayVisaSeries.setValidators([Validators.required, Validators.maxLength(2)]);
      step4Controls.stayVisaNumber.setValidators([Validators.required, Validators.maxLength(10), isNumericValidator]);
      step4Controls.stayVisaIssueDate.setValidators([Validators.required, customDateValidator]);
      step4Controls.stayVisaExpiryDate.setValidators([Validators.required, customDateValidator]);
      step4Controls.stayVisaPeriodOfStay.setValidators([Validators.required, Validators.min(1), Validators.max(9999)]);
      step4Controls.stayVisaIssuingAuthority.setValidators([Validators.required, Validators.maxLength(100)]);
    }

    if (legalBasisOfStay !== 'VISA') {
      step4Controls.stayVisaType.reset(null);
      step4Controls.stayVisaSeries.reset(null);
      step4Controls.stayVisaNumber.reset(null);
      step4Controls.stayVisaIssueDate.reset(null);
      step4Controls.stayVisaExpiryDate.reset(null);
      step4Controls.stayVisaPeriodOfStay.reset(null);
      step4Controls.stayVisaIssuingAuthority.reset(null);

      step4Controls.stayVisaType.setValidators([]);
      step4Controls.stayVisaSeries.setValidators([]);
      step4Controls.stayVisaNumber.setValidators([]);
      step4Controls.stayVisaIssueDate.setValidators([]);
      step4Controls.stayVisaExpiryDate.setValidators([]);
      step4Controls.stayVisaPeriodOfStay.setValidators([]);
      step4Controls.stayVisaIssuingAuthority.setValidators([]);
    }

    if (legalBasisOfStay === 'SCHENGEN_COUNTRY_ISSUED_DOC') {
      step4Controls.stayEntitlingDocumentIssuedBy.setValidators([Validators.required, Validators.maxLength(100)]);
      step4Controls.stayEntitlingDocumentIssueDate.setValidators([Validators.required, customDateValidator]);
    } else {
      step4Controls.stayEntitlingDocumentIssuedBy.reset(null);
      step4Controls.stayEntitlingDocumentIssueDate.reset(null);
      step4Controls.stayEntitlingDocumentIssuedBy.setValidators([]);
      step4Controls.stayEntitlingDocumentIssueDate.setValidators([]);
    }

    if (legalBasisOfStay !== 'VISA' && legalBasisOfStay !== 'VISA_FREE') {
      step4Controls.entryDocumentSeries.setValidators([
        Validators.maxLength(2),
        Validators.pattern(patterns.PLTextValidator),
      ]);
      step4Controls.entryDocumentNumber.setValidators([
        Validators.minLength(1),
        Validators.maxLength(20),
        isNumericValidator,
      ]);
      step4Controls.entryDocumentIssueDate.setValidators([customDateValidator]);
      step4Controls.entryDocumentExpiryDate.setValidators([customDateValidator]);
      step4Controls.entryDocumentIssuingAuthority.setValidators([Validators.maxLength(100)]);
    }

    if (legalBasisOfStay === 'VISA' || legalBasisOfStay === 'VISA_FREE') {
      step4Controls.entryDocumentSeries.reset(null);
      step4Controls.entryDocumentNumber.reset(null);
      step4Controls.entryDocumentIssueDate.reset(null);
      step4Controls.entryDocumentExpiryDate.reset(null);
      step4Controls.entryDocumentIssuingAuthority.reset(null);

      step4Controls.entryDocumentSeries.setValidators([]);
      step4Controls.entryDocumentNumber.setValidators([]);
      step4Controls.entryDocumentIssueDate.setValidators([]);
      step4Controls.entryDocumentExpiryDate.setValidators([]);
      step4Controls.entryDocumentIssuingAuthority.setValidators([]);
    }

    step4Controls.stayVisaType.markAsUntouched();
    step4Controls.stayVisaType.updateValueAndValidity();

    step4Controls.stayVisaSeries.markAsUntouched();
    step4Controls.stayVisaSeries.updateValueAndValidity();

    step4Controls.stayVisaNumber.markAsUntouched();
    step4Controls.stayVisaNumber.updateValueAndValidity();

    step4Controls.stayVisaIssueDate.markAsUntouched();
    step4Controls.stayVisaIssueDate.updateValueAndValidity();

    step4Controls.stayVisaExpiryDate.markAsUntouched();
    step4Controls.stayVisaExpiryDate.updateValueAndValidity();

    step4Controls.stayVisaPeriodOfStay.markAsUntouched();
    step4Controls.stayVisaPeriodOfStay.updateValueAndValidity();

    step4Controls.stayVisaIssuingAuthority.markAsUntouched();
    step4Controls.stayVisaIssuingAuthority.updateValueAndValidity();

    step4Controls.stayEntitlingDocumentIssuedBy.markAsUntouched();
    step4Controls.stayEntitlingDocumentIssuedBy.updateValueAndValidity();

    step4Controls.stayEntitlingDocumentIssueDate.markAsUntouched();
    step4Controls.stayEntitlingDocumentIssueDate.updateValueAndValidity();

    step4Controls.entryDocumentSeries.markAsUntouched();
    step4Controls.entryDocumentSeries.updateValueAndValidity();

    step4Controls.entryDocumentNumber.markAsUntouched();
    step4Controls.entryDocumentNumber.updateValueAndValidity();

    step4Controls.entryDocumentIssueDate.markAsUntouched();
    step4Controls.entryDocumentIssueDate.updateValueAndValidity();

    step4Controls.entryDocumentExpiryDate.markAsUntouched();
    step4Controls.entryDocumentExpiryDate.updateValueAndValidity();

    step4Controls.entryDocumentIssuingAuthority.markAsUntouched();
    step4Controls.entryDocumentIssuingAuthority.updateValueAndValidity();
  }

  public toggleApplicantHaveBlueCardStaysInEU(value: boolean): void {
    const step4Controls = this.personalDataForm.controls['step4'].controls;

    if (value) {
      step4Controls.blueCardStaysInEU.setValidators([Validators.required, Validators.maxLength(9999)]);
    }

    if (!value) {
      step4Controls.blueCardStaysInEU.reset(null);
      step4Controls.blueCardStaysInEU.setValidators([]);
    }

    step4Controls.blueCardStaysInEU.markAsUntouched();
    step4Controls.blueCardStaysInEU.updateValueAndValidity();
  }

  public toggleApplicantHaveScientistStaysInEU(value: boolean): void {
    const step4Controls = this.personalDataForm.controls['step4'].controls;

    if (value) {
      step4Controls.scientistStaysInEU.setValidators([Validators.required, Validators.maxLength(9999)]);
    }

    if (!value) {
      step4Controls.scientistStaysInEU.reset(null);
      step4Controls.scientistStaysInEU.setValidators([]);
    }

    step4Controls.scientistStaysInEU.markAsUntouched();
    step4Controls.scientistStaysInEU.updateValueAndValidity();
  }

  public toggleApplicantHaveInternationalProtectionStaysInEU(value: boolean): void {
    const step4Controls = this.personalDataForm.controls['step4'].controls;

    if (value) {
      step4Controls.internationalProtectionStaysInEU.setValidators([Validators.required, Validators.maxLength(9999)]);
    }

    if (!value) {
      step4Controls.internationalProtectionStaysInEU.reset(null);
      step4Controls.internationalProtectionStaysInEU.setValidators([]);
    }

    step4Controls.internationalProtectionStaysInEU.markAsUntouched();
    step4Controls.internationalProtectionStaysInEU.updateValueAndValidity();
  }

  public toggleApplicantHaveHighQualificationsStaysInEU(value: boolean): void {
    const step4Controls = this.personalDataForm.controls['step4'].controls;

    if (value) {
      step4Controls.highQualificationsStaysInEU.setValidators([Validators.required, Validators.maxLength(9999)]);
    }

    if (!value) {
      step4Controls.highQualificationsStaysInEU.reset(null);
      step4Controls.highQualificationsStaysInEU.setValidators([]);
    }

    step4Controls.highQualificationsStaysInEU.markAsUntouched();
    step4Controls.highQualificationsStaysInEU.updateValueAndValidity();
  }

  public toggleApplicantHaveStudentStaysInEU(value: boolean): void {
    const step4Controls = this.personalDataForm.controls['step4'].controls;

    if (value) {
      step4Controls.studentStaysInEU.setValidators([Validators.required, Validators.maxLength(9999)]);
    }

    if (!value) {
      step4Controls.studentStaysInEU.reset(null);
      step4Controls.studentStaysInEU.setValidators([]);
    }

    step4Controls.studentStaysInEU.markAsUntouched();
    step4Controls.studentStaysInEU.updateValueAndValidity();
  }

  public toggleApplicantHaveTravelsWithBlueCardStaysInEU(value: boolean): void {
    const step4Controls = this.personalDataForm.controls['step4'].controls;

    if (value) {
      step4Controls.travelsWithBlueCardStaysInEU.setValidators([Validators.required, Validators.maxLength(9999)]);
    }

    if (!value) {
      step4Controls.travelsWithBlueCardStaysInEU.reset(null);
      step4Controls.travelsWithBlueCardStaysInEU.setValidators([]);
    }

    step4Controls.travelsWithBlueCardStaysInEU.markAsUntouched();
    step4Controls.travelsWithBlueCardStaysInEU.updateValueAndValidity();
  }

  public toggleApplicantOtherLongTermPermits(value: boolean): void {
    const step4Controls = this.personalDataForm.controls['step4'].controls;

    if (value) {
      step4Controls.otherLongTermPermits.setValidators([Validators.required, Validators.maxLength(9999)]);
    }

    if (!value) {
      step4Controls.otherLongTermPermits.reset(null);
      step4Controls.otherLongTermPermits.setValidators([]);
    }

    step4Controls.otherLongTermPermits.markAsUntouched();
    step4Controls.otherLongTermPermits.updateValueAndValidity();
  }

  private toggleCurrentlyLivingInPoland(currentlyLivingInPoland: boolean): void {
    const step4Controls = this.personalDataForm.controls['step4'].controls;

    if (currentlyLivingInPoland === true) {
      step4Controls.lastEntryDate.setValidators([Validators.required, customDateValidator, forbiddenFutureDate()]);
    }

    if (currentlyLivingInPoland === false) {
      step4Controls.lastEntryDate.setValidators([Validators.required, customDateValidator, forbiddenDateInPast()]);
    }

    step4Controls.lastEntryDate.updateValueAndValidity();
  }

  private checkViewport(): void {
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    this.isSmallView = viewportHeight < 450;
  }
}
